import React, { useEffect, useRef, useState } from 'react';
import classes from './ErrorDisplay.module.scss';
import { connect } from 'react-redux';
import { clearError, clearFormError, stopTimer } from 'js/actions';
import { IGNORED_ACTION_TYPES_IN_ERROR_DISPLAY } from 'js/constants/requestTypes';
import {
  Collapse,
  LinearProgress,
  linearProgressClasses,
  Paper,
  Slide,
  Snackbar,
  Stack,
  styled,
} from '@mui/material';
import { ErrorOutline, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import Typography from 'new/elements/Typography';
import { Button } from 'new/elements/Button';
import { IconButton } from 'new/elements/IconButton/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { interpolateCSSVariable } from 'new/theme';
import useInterval from 'new/hooks/useInterval';
import { TransitionProps } from '@mui/material/transitions';

const IGNORED_STATUS_CODES = [401];

// Creating our own ERROR_TIMEOUT to sync animations;
const ERROR_TIMEOUT = 4400;

type Error = {
  type: string;
  code: number;
  error: string;
  message: string;
  /** Optional custom header; defaults to "Error" when absent. */
  title?: string;
  severity: string;
  request: { endpoint: string; payload: any };
};

export type ErrorDisplayProps = {
  error: Error;
  formType?: string;
  errorStatus: boolean;
  isFormError: boolean;
  clearError: () => void;
  clearFormError: (a: string) => void;
};

const LinearTimerView = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 3,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: 'transparent',
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 3,
    transition: 'transform 0.1s linear',
    backgroundColor: interpolateCSSVariable('--color-danger-700'),
  },
}));

function TransitionLeft(props: TransitionProps) {
  // @ts-ignore
  return <Slide {...props} direction="left" />;
}

function ErrorAlert({ error, onClose }) {
  const errorMessage = useRef<HTMLParagraphElement>(null);

  const [progress, setProgress] = useState(100);
  const [expand, setExpand] = useState(false);
  const [useCollapse, setUseCollapse] = useState(false);
  const [minHeight, setMinHeight] = useState(0);

  const { startInterval, pauseInterval } = useInterval(() => {
    setProgress((p) => {
      let remaining = (p * ERROR_TIMEOUT) / 100;
      remaining -= 100;
      return (remaining * 100) / ERROR_TIMEOUT;
    });
  }, 100);

  useEffect(() => {
    if (progress <= 0) {
      setTimeout(() => {
        onClose();
      }, 200);
    }
  }, [progress]);

  useEffect(() => {
    startInterval();
    return () => {
      pauseInterval();
    };
  }, []);

  useEffect(() => {
    const elHeight = errorMessage.current.offsetHeight;
    const css = getComputedStyle(errorMessage.current);
    const lineHeight = Number(css.lineHeight.replace('px', ''));
    const lines = elHeight / lineHeight;
    if (lines > 2) {
      setMinHeight(lineHeight * 2);
      setUseCollapse(true);
    } else {
      setUseCollapse(false);
    }
  }, []);

  const onHover = () => {
    stopTimer();
    pauseInterval();
  };

  const onLeave = () => {
    startInterval();
  };

  const CollapseWrapper = useCollapse ? Collapse : React.Fragment;

  const collapseProps = useCollapse
    ? {
        in: expand,
        collapsedSize: minHeight,
      }
    : {};

  return (
    <Snackbar
      data-testid="error-alert"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      open={progress > 1}
      TransitionComponent={TransitionLeft}
    >
      <Paper elevation={6} className={classes.container}>
        <Stack onMouseEnter={onHover} onMouseLeave={onLeave}>
          <LinearTimerView variant="determinate" value={progress} />
          <Stack direction="row" marginTop="5px">
            <ErrorOutline color="error" />
            <Stack marginLeft="10px" width="500px">
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle1">{error?.title || 'Error'}</Typography>
                <Stack direction="row" alignItems="center">
                  {minHeight ? (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => setExpand((expand) => !expand)}
                      endIcon={expand ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
                    >
                      {expand ? 'Show less' : 'Show more'}
                    </Button>
                  ) : null}
                  <IconButton
                    onClick={() => {
                      setProgress(1);
                      setTimeout(() => {
                        onClose();
                      }, 200);
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Stack>
              </Stack>
              <CollapseWrapper {...collapseProps}>
                <Typography className={classes.message} ref={errorMessage} variant="body2">
                  {error?.message}
                </Typography>
              </CollapseWrapper>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </Snackbar>
  );
}

function ErrorDisplay(props: ErrorDisplayProps) {
  const {
    errorStatus = false,
    error = { message: '', code: '', type: '' },
    isFormError = false,
    formType = null,
    clearFormError,
    clearError,
  } = props;

  const _onErrorCleared = () => {
    if (isFormError === true) {
      clearFormError?.(formType);
    } else {
      clearError?.();
    }
  };

  return (
    errorStatus === true &&
    error &&
    !IGNORED_STATUS_CODES.includes(error.code) &&
    !IGNORED_ACTION_TYPES_IN_ERROR_DISPLAY.includes(error.type) && (
      <ErrorAlert error={error} onClose={_onErrorCleared} />
    )
  );
}

export default connect(null, {
  clearError,
  clearFormError,
})(ErrorDisplay);
